<script setup lang="ts">
import { CommonFeedbackRequest } from '~/api/repository'
import { feedbackService } from '~/api/service/feedback.service'
import { useFetchSearchHandler } from '~/api/service/search/handler/index.service'
import IconButton from '~/components/button/IconButton.vue'
import Spinner from '~/components/loading/Spinner.vue'
import ResultList from '~/components/resultList/ResultList.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { searchFilterType } from '~/types/search.type'
import BcSearchInput from '~/ui/Input/BcSearchInput.vue'
import BcKeyboard from '~/ui/Keyboard/BcKeyboard.vue'
import SearchSelect from '~/ui/Select/SearchSelect.vue'
import feedbackType = CommonFeedbackRequest.feedbackType;

const router = useRouter()
const { changeShowModal } = useModal()
const { mutate, isSuccess } = feedbackService.useCreateFeedback()

const inputModel = ref('')
const filterModel = ref<searchFilterType>(searchFilterType.ALL)
const isSearchEnable = computed(() => inputModel.value.length >= 2)
const { data, isError, isLoading, refetch } = useFetchSearchHandler(inputModel, filterModel, isSearchEnable)

const searchRedirect = () => {
  if (!inputModel.value) {
    return
  }
  // block
  if (parseInt(inputModel.value).toString() === inputModel.value) {
    router.push(routes.getBlockDetailPageWithTab(inputModel.value))
    return
  }
  if (inputModel.value.slice(0, 2) === '0x' && data.value?.totalCount === 1) {
    // transaction
    if (data.value.resultMap.get(searchFilterType.TRANSACTION)?.length === 1) {
      router.push(routes.getTransactionDetailPageWithTab(data.value.resultMap.get(searchFilterType.TRANSACTION)?.[0] as string))
      return
    }
    // account
    if (data.value.resultMap.get(searchFilterType.ADDRESS)?.length === 1) {
      router.push(routes.getAccountDetailPageWithTab(data.value.resultMap.get(searchFilterType.ADDRESS)?.[0] as string))
      return
    }
  }
}

const changeFilter = () => {
  if (inputModel.value.length) {
    refetch()
  }
}

const sendFeedback = () => {
  if (!inputModel.value) {
    return
  }
  mutate({
    type: feedbackType.SEARCH_REPORT,
    comment:
      `Search result not found: ${inputModel.value} in ${searchFilterType[filterModel.value]}`,
  })
}
</script>

<template>
  <div
    class="search-modal"
    @click.stop=""
  >
    <div
      v-if="isSuccess"
      class="submitted-result"
    >
      <IconButton
        icon="times"
        class="close-button"
        size="lg"
        @click="changeShowModal(false)"
      />
      <img
        :src="IMAGE_BASE_URL + '/character/feedback/submitted.png'"
        alt="feedback submitted"
        width="192"
      >
      <div class="flex flex-col items-center gap-y-2">
        <p class="text-2xl font-bold text-primary">
          Thank you!
        </p>
        <p class="text-lg text-primary/40">
          Your feedback has been successfully submitted.
        </p>
      </div>
    </div>
    <template v-else>
      <div class="search-bar">
        <SearchSelect
          v-model="filterModel"
          @change="changeFilter"
          class="text-lg"
        />
        <BcSearchInput
          v-model="inputModel"
          @search-redirect="searchRedirect"
          type="search"
          class="grow"
        >
          <template #suffix>
            <BcKeyboard value="ESC" class="ml-4" />
          </template>
        </BcSearchInput>
      </div>
        <hr class="border-primary/10"/>
        <CustomSuspense
          :is-loading="isLoading && isSearchEnable"
          :is-empty="isError || !isSearchEnable || !data?.totalCount"
        >
          <template #loading>
            <div class="p-8 text-primary/40">
              <Spinner />
            </div>
          </template>
          <template #empty>
            <div class="flex flex-col text-primary/40">
              <span class="p-8">No search result<template v-if="inputModel"> for "<span class="text-primary">{{
                inputModel
              }}</span>"</template>.</span>
              <p
                v-if="inputModel"
                class="rounded-b-2xl flex flex-col gap-y-8 p-8 bg-primary/10 text-base"
              >
              <div class="flex flex-col gap-y-4">
                <span class="text-lg font-bold">Can’t find what you’re looking for? Here are some common reasons:</span>
                <ul class="p-2 rounded-lg flex flex-col bg-primary/5 text-primary">
                  <li class="list-disc ml-4">Game data may take some time to appear. Please check back shortly.</li>
                  <li class="list-disc ml-4">NXPC airdrops were distributed via Avalanche C-Chain, which is not supported by this explorer.</li>
                  <li class="list-disc ml-4">This explorer is not operated by the MapleStory N team. For in-game issues, please contact their official support channels.</li>
                  <li class="list-disc ml-4">Unfortunately, we are unable to help with transactions sent to incorrect addresses.</li>
                </ul>
              </div>
              <div class="flex items-center gap-x-1">
                If none of these apply, feel free to
                <button
                  class="text-primary underline"
                  @click="sendFeedback"
                >
                  report an issue.
                </button>
              </div>
              </p>
            </div>
          </template>
          <template #default>
            <div class="result-container">
              <div class="result-list">
                <ResultList
                  title="Block Hash"
                  icon="cube"
                  :result-list="data?.resultMap.get(searchFilterType.BLOCK) ?? []"
                  :keyword="inputModel"
                  :type="searchFilterType.BLOCK"
                />
                <ResultList
                  title="Transaction Hash"
                  icon="exchange-alt"
                  :result-list="data?.resultMap.get(searchFilterType.TRANSACTION) ?? []"
                  :keyword="inputModel"
                  :type="searchFilterType.TRANSACTION"
                />
                <ResultList
                  title="Account"
                  icon="wallet"
                  :result-list="data?.resultMap.get(searchFilterType.ADDRESS) ?? []"
                  :keyword="inputModel"
                  :type="searchFilterType.ADDRESS"
                />
                <ResultList
                  title="Token / NFT"
                  :result-list="data?.resultMap.get(searchFilterType.TOKEN) ?? []"
                  :keyword="inputModel"
                  :type="searchFilterType.TOKEN"
                />
              </div>
            </div>
          </template>
        </CustomSuspense>
    </template>
  </div>
</template>

<style scoped lang="scss">
.search-modal {
  @apply absolute top-0 left-1/2 mx-auto w-[calc(100%-2rem)] max-w-[870px] -translate-x-1/2 rounded-2xl bg-white/10
  z-10 max-h-[75vh];

  &::before {
    @apply absolute top-0 left-0 w-full h-full z-[-1] backdrop-blur-3xl rounded-2xl;
    content: "";
  }

  @include sm {
    @apply max-h-[calc(100vh-2rem)];
  }

  .search-bar {
    @apply flex gap-x-4 px-8 py-4;

    @include sm {
      @apply gap-x-2 px-4;
    }
  }

  .result-container {
    @apply overflow-y-auto border-t border-primary/10 px-8 py-4 max-h-[calc(75vh-72px)];

    @include custom-scrollbar;

    @include sm {
      @apply px-4 max-h-[calc(100vh-104px)];
    }

    .result-list {
      @apply flex flex-col gap-y-8 overflow-y-auto;
    }
  }
}

.submitted-result {
  @apply relative flex h-[480px] flex-col items-center justify-center gap-y-8 p-8 text-center;

  @include sm {
    @apply gap-y-4 px-4 h-full;
  }

  .close-button {
    @apply absolute right-8 top-4 text-primary;

    @include sm {
      @apply right-4;
    }
  }
}
</style>